import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface CreditErrorData {
  credit_error: string;
  message: string;
  household_balance?: number;
  limit?: number;
  spent?: number;
  period?: string;
}

export default function CreditErrorDialog() {
  const [error, setError] = useState<CreditErrorData | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CreditErrorData>).detail;
      setError(detail);
    };
    window.addEventListener('credit-error', handler);
    return () => window.removeEventListener('credit-error', handler);
  }, []);

  if (!error) return null;

  const close = () => setError(null);

  const goToCredits = () => {
    setError(null);
    navigate('/settings/credits');
  };

  // Dialog content based on error type
  let title = '';
  let body = '';
  let showCreditsLink = false;

  switch (error.credit_error) {
    case 'MEMBER_LIMIT_EXCEEDED':
      title = 'Dein Limit ist erreicht';
      body = `Du hast ${error.spent || 0}/${error.limit || 0} Credits ${
        error.period === 'week' ? 'diese Woche' : 'diesen Monat'
      } verbraucht. Wende dich an den Haushalt-Admin.`;
      break;
    case 'INSUFFICIENT_CREDITS':
      title = 'Nicht genug Credits';
      body = `Dein Haushalt hat nur noch ${error.household_balance ?? 0} Credits. Für diese Aktion reicht das leider nicht.`;
      showCreditsLink = true;
      break;
    case 'PERMISSION_DENIED':
      title = 'Keine Berechtigung';
      body = error.message || 'Du hast keine Berechtigung für diese Aktion. Wende dich an den Haushalt-Admin.';
      break;
    case 'INACTIVE_MEMBER':
      title = 'Mitgliedschaft deaktiviert';
      body = 'Deine Mitgliedschaft ist deaktiviert. Wende dich an den Haushalt-Admin.';
      break;
    default:
      title = 'Credit-Fehler';
      body = error.message || 'Ein Fehler ist aufgetreten.';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-warm-900">{title}</h3>
        <p className="text-sm text-warm-500">{body}</p>
        <div className="flex gap-2 pt-1">
          <button onClick={close}
            className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
            Schliessen
          </button>
          {showCreditsLink && (
            <button onClick={goToCredits}
              className="flex-1 py-2.5 bg-teal-500 text-white rounded-xl text-sm font-medium hover:bg-teal-600 transition-colors">
              Credits anzeigen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Dispatch a credit error event from anywhere in the app.
 * Call this in catch blocks when you get a 402 response.
 */
export function showCreditError(error: CreditErrorData) {
  window.dispatchEvent(new CustomEvent('credit-error', { detail: error }));
}
